import { useState } from "react";
import { Alert, Dialog, Button, Select, Row } from "@narmi/design_system";

import { RETURN_ACCEPT_REASONS, RETURN_ACCEPT_REASONS_TYPE } from "./codesets";
import { Payment } from "./PaymentTable";
import { CSRF_TOKEN } from "@pages/csrf";

export default function PostingFailedDialog({
  payment,
  action,
  setAction,
  successCallback,
}: {
  payment: Payment;
  action: "return" | "manually_resolve" | null;
  setAction: CallableFunction;
  successCallback: CallableFunction;
}) {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [disableSubmit, setDisableSubmit] = useState(false);

  async function onSubmit() {
    setDisableSubmit(true);
    const path = `/v1/fednow/payments/${payment.payment_identification}/${action}_post_failed`;
    const data = { reason: reason };
    const res = await fetch(path, {
      method: "POST",
      body: action === "return" ? JSON.stringify(data) : undefined,
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": CSRF_TOKEN,
      },
    });
    if (!res.ok) {
      const resData = await res.json();
      if (typeof resData.message === "string") {
        setError(resData.message);
      } else {
        setError(Object.values(resData)[0] as string);
      }
      setDisableSubmit(false);
      return;
    }
    successCallback();
    setDisableSubmit(false);
    setAction(null);
  }

  const title = action === "return" ? "Return payment in full" : "Manually resolve payment";
  let description;
  if (action === "return") {
    if (payment?.state === "final_post_failed") {
      description =
        "The payment will be returned to the sender in full. The previously deposited funds will be withdrawn from the core account.";
    } else {
      description =
        "The payment will be returned to the sender in full. No withdrawal will be made from the core since posting was not successful.";
    }
  } else {
    description = "Mark this payment as manually resolved. No automated action will be taken.";
  }
  return (
    <Dialog isOpen={action !== null} title={title} onUserDismiss={() => setAction(null)}>
      {error && (
        <div className="padding--bottom--s">
          <Alert isActive kind="error">
            {error}
          </Alert>
        </div>
      )}
      <p>{description}</p>

      {action === "return" && (
        <div className="padding--bottom--s">
          <Select
            label="Return reason"
            onChange={(val: string) => {
              setReason(val);
            }}
          >
            {Object.keys(RETURN_ACCEPT_REASONS).map((r) => {
              return (
                <Select.Item value={r}>
                  {RETURN_ACCEPT_REASONS[r as RETURN_ACCEPT_REASONS_TYPE]}
                </Select.Item>
              );
            })}
          </Select>
        </div>
      )}

      <Row justifyContent="end" alignItems="center">
        <Row.Item shrink>
          <Button
            label="Confirm"
            onClick={onSubmit}
            disabled={disableSubmit || (action === "return" && !reason)}
          />
        </Row.Item>
      </Row>
    </Dialog>
  );
}
